import { useEffect } from 'react';
import { connect } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import PropTypes from 'prop-types';
import { updateAccessToken, clearAccessToken } from '../../actions/authenticationActions';
import { TANGO_CLIENT_ID } from '../../constants';
import {
  registerAuth0TokenFetcher,
  clearAuth0TokenFetcher,
  setCachedAuth0AccessToken,
  isAuth0AccessTokenExpired,
} from '../../auth/auth0TokenBridge';

const AUTH_TOKEN_STORAGE_KEY = 'authToken';
const REQUIRED_API_SCOPE = 'TradeStation';
const REQUIRED_OFFLINE_ACCESS_SCOPE = 'offline_access';

const buildScope = (scope) => {
  const existingScopes = (scope || 'openid profile').split(/\s+/).filter(Boolean);
  if (!existingScopes.includes(REQUIRED_API_SCOPE)) {
    existingScopes.push(REQUIRED_API_SCOPE);
  }
  if (!existingScopes.includes(REQUIRED_OFFLINE_ACCESS_SCOPE)) {
    existingScopes.push(REQUIRED_OFFLINE_ACCESS_SCOPE);
  }

  return existingScopes.join(' ');
};

const Auth0TokenSync = (props) => {
  const {
    updateAccessToken,
    clearAccessToken,
    currentAuthToken,
    isTangoClient,
    authAudience,
    authScope,
  } = props;

  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
  } = useAuth0();

  useEffect(() => {
    if (!isTangoClient || isLoading) {
      clearAuth0TokenFetcher();
      return undefined;
    }

    let mounted = true;

    const syncAccessToken = async () => {
      try {
        const tokenOptions = {
          authorizationParams: {
            scope: buildScope(authScope),
          },
        };

        if (authAudience) {
          tokenOptions.authorizationParams.audience = authAudience;
        }

        const token = await getAccessTokenSilently(tokenOptions);
        if (mounted && token) {
          sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
          setCachedAuth0AccessToken(token);
          if (token !== currentAuthToken) {
            updateAccessToken(token);
          }
        }
      } catch (error) {
        const hasValidToken = currentAuthToken && !isAuth0AccessTokenExpired(currentAuthToken);
        if (mounted && !hasValidToken) {
          sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
          clearAccessToken();
        }
      }
    };

    registerAuth0TokenFetcher(async () => {
      const tokenOptions = {
        authorizationParams: {
          scope: buildScope(authScope),
        },
      };

      if (authAudience) {
        tokenOptions.authorizationParams.audience = authAudience;
      }

      return getAccessTokenSilently(tokenOptions);
    });

    const shouldSyncToken = !currentAuthToken || isAuth0AccessTokenExpired(currentAuthToken);
    if (isAuthenticated && shouldSyncToken) {
      syncAccessToken();
    }

    return () => {
      mounted = false;
      clearAuth0TokenFetcher();
    };
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    updateAccessToken,
    clearAccessToken,
    currentAuthToken,
    isTangoClient,
    authAudience,
    authScope,
  ]);

  return null;
};

Auth0TokenSync.propTypes = {
  updateAccessToken: PropTypes.func.isRequired,
  clearAccessToken: PropTypes.func.isRequired,
  currentAuthToken: PropTypes.string,
  isTangoClient: PropTypes.bool.isRequired,
  authAudience: PropTypes.string,
  authScope: PropTypes.string,
};

export default connect(
  state => ({
    currentAuthToken: state.authentication.authToken,
    isTangoClient: state.clientId === TANGO_CLIENT_ID,
    authAudience: state.configuration.authAudience,
    authScope: state.configuration.authScope,
  }),
  {
    updateAccessToken,
    clearAccessToken,
  }
)(Auth0TokenSync);